import React, {type ReactNode} from 'react';
import {
  emotionStyled,
  type BreakpointPropFor,
} from '@packages/utilities/styled';
import {COLUMNS_CUSTOM_SPACING_VAR} from './Columns';

import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

type ColumnWidthBase = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type ColumnWidth = ColumnWidthBase | 'auto';

export type ColumnProps = {
  /**
   * Content for Column.
   */
  children?: ReactNode;
  /**
   * (Left) offset for the column.
   */
  offset?: BreakpointPropFor<ColumnWidthBase>;
  /**
   * Width of the column.
   */
  width?: BreakpointPropFor<ColumnWidth>;
} & HTMLAttributes &
  SpringTooling;

const GRID_COLUMN_SIZE = 12;
const COLUMN_SPACING_VAR = '--column-spacing';

const getWidthValue = (width: number) => {
  if (width === GRID_COLUMN_SIZE) return '100%';
  return `${((width / GRID_COLUMN_SIZE) * 100)
    .toFixed(4)
    .replace(/\.0000$/, '')}%`;
};

const generateColumns = () => Array(GRID_COLUMN_SIZE + 1).fill(true);

const getWidthVariants = () => {
  const variants = {
    auto: {
      maxWidth: '100%',
      flex: '1 1 0',
    },
  } as const;

  generateColumns().forEach((_, index) => {
    const widthValue = getWidthValue(index);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ readonly auto: { readonly maxWidth: "100%"; readonly flex: "1 1 0"; }; }'.
    variants[index] = {
      maxWidth: widthValue,
      flex: `0 0 ${widthValue}`,
    };
  });

  return variants;
};

const getOffsetVariants = () => {
  const variants: Record<string, any> = {};

  generateColumns().forEach((_, index) => {
    const widthValue = getWidthValue(index);
    variants[index] = {
      // Left
      marginInlineStart: widthValue,
    };
  });

  return variants;
};

const ColumnView = emotionStyled('div')(
  {
    [COLUMN_SPACING_VAR]: `var(${COLUMNS_CUSTOM_SPACING_VAR})`,
    minHeight: 1,
    position: 'relative',
    width: '100%',
    // Top
    paddingBlockStart: `var(${COLUMN_SPACING_VAR})`,
    // Left
    paddingInlineStart: `var(${COLUMN_SPACING_VAR})`,
    maxWidth: '100%',
    flex: '1 1 0',
  },
  {
    componentName: 'Column',
    variants: {
      offset: getOffsetVariants(),
      width: getWidthVariants(),
    },
  }
);

/**
 * # Column
 *
 * The Spring Grid System is a mobile-first layout system based on a
 * 12-column grid layout.
 *
 * `Column` renders content within a 12-column grid layout. Their width values
 * are based on a base of `12`. For example, to render content at half (50%) the
 * area, the column width value would be `6` (6 / 12 = 50%).
 *
 * ## Usage
 *
 * The Grid System works by combining a `Columns` wrapper along with inner
 * `Column` components. This is often wrapped with a `Container`.
 *
 * ```jsx
 * import {Container, Columns, Column, Card} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *     <Container>
 *       <Columns>
 *         <Column width={4}>
 *           <Card>...</Card>
 *         </Column>
 *         <Column width={4}>
 *           <Card>...</Card>
 *         </Column>
 *         <Column width={4}>
 *           <Card>...</Card>
 *         </Column>
 *       </Columns>
 *     </Container>
 *   );
 * }
 * ```
 */
export const Column = React.forwardRef<HTMLDivElement, ColumnProps>(
  ({...props}: ColumnProps, ref) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    return <ColumnView {...props} ref={ref} />;
  }
);

Column.displayName = 'Column';
Column.toString = ColumnView.toString;

export default Column;
